import Accordion from "@/common/components/Accordion"
import Button from "@/common/components/Button"
import MaskedTextField from "@/common/components/MaskedTextField"
import TextField from "@/common/components/TextField"
import { useEquipmentCategories } from "@/common/hooks/queries/useEquipmentCategories"
import { Mapper } from "@/common/services/mapper"
import { EquipmentCategoryData } from "@/features/equipmentModels/types/equipmentCategories"
import { PrivateCompanyLeadsFunnelUpdateContractValidationSchema } from "@/features/privateCompanyLeadsFunnel/schemas/update.schema"
import { ContractData, OperationalInformationData } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { useQueryClient } from "@tanstack/react-query"
import { XIcon } from "lucide-react"
import { Control, Controller, FieldErrors, useFieldArray, useFormContext } from "react-hook-form"
import useCreatePrivateCompanyLeadContractEquipmentCategory from '../hooks/useCreatePrivateCompanyLeadContractEquipmentCategory'
import { useParams } from 'react-router-dom'
import { Dispatch, SetStateAction, useState } from 'react'
import { Option } from '@/common/types/option'
import Select from '@/common/components/Select'
import useDeletePrivateCompanyLeadEquipmentCategory from '../hooks/useDeletePrivateCompanyLeadEquipmentCategory'
import useDebounce from "@/common/hooks/useDebounce"

interface ContractEquipmentsProps {
  contract?: ContractData
  operational_information?: OperationalInformationData
  control: Control<PrivateCompanyLeadsFunnelUpdateContractValidationSchema, any>
  errors: FieldErrors<PrivateCompanyLeadsFunnelUpdateContractValidationSchema>
  submittableOnBlur: (e: React.FocusEvent<HTMLInputElement, Element>, onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void) => void
  disableFields?: boolean
  setServerError: Dispatch<SetStateAction<boolean>>
}

export const ContractEquipments = ({
  contract,
  operational_information,
  control,
  errors,
  submittableOnBlur,
  disableFields,
  setServerError,
}: ContractEquipmentsProps) => {
  const { privateCompanyLeadId } = useParams()

  const queryClient = useQueryClient()

  const { watch } = useFormContext<PrivateCompanyLeadsFunnelUpdateContractValidationSchema>()

  const [categoryOptionToAppend, setCategoryOptionToAppend] = useState<Option>()
  const [categoryIndexToRemove, setCategoryIndexToRemove] = useState<number>()

  const [searchEquipmentCategory, setSearchEquipmentCategory] = useDebounce('')

  const {
    mutate: createPrivateCompanyLeadContractEquipmentCategory,
    isPending: createPrivateCompanyLeadContractEquipmentCategoryIsPending,
  } = useCreatePrivateCompanyLeadContractEquipmentCategory({
    privateCompanyLeadId,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLeadId] })

      if (categoryOptionToAppend) {
        const createdContractEquipmentCategory = response.data.data?.contract?.contract_equipment_categories?.find(
          (category) => category.equipment_category.id === categoryOptionToAppend.value
        );

        appendEquipmentCategory({
          id: createdContractEquipmentCategory?.id || '',
          equipment_category: {
            id: categoryOptionToAppend.value,
            display_name: categoryOptionToAppend.label,
          },
          quantity: '1',
        })

        setCategoryOptionToAppend(undefined)
      }
    },
    onError: () => {
      setServerError(true);
    },
  })

  const { mutate: deletePrivateCompanyLeadEquipmentCategory } = useDeletePrivateCompanyLeadEquipmentCategory({
    privateCompanyLeadId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLeadId] })

      if (categoryIndexToRemove !== undefined) {
        removeEquipmentCategory(categoryIndexToRemove)

        setCategoryIndexToRemove(undefined)
      }
    },
    onError: () => {
      setServerError(true);
    },
  })

  const { data: equipmentCategoryOptions } = useEquipmentCategories({
    filters: { search: searchEquipmentCategory },
    mapper: Mapper.mapToOptions<EquipmentCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const defined_equipment_count = contract?.contract_equipment_categories?.reduce(
    (acc, { quantity }) => acc + quantity,
    0
  ) || 0

  const {
    append: appendEquipmentCategory,
    remove: removeEquipmentCategory,
  } = useFieldArray({
    control,
    name: 'contract.contract_equipment_categories',
  })

  return (
    <Accordion
      className="py-1"
      contentClassName="data-[state=open]:overflow-visible"
      head={
        <p>Equipamentos <span className="text-danger-soft"> *</span></p>
      }
      body={
        <div className="flex flex-col gap-4">
          <Select
            placeholder="Pesquisar Categorias de Equipamentos"
            value={null}
            onChange={(newValue) => {
              createPrivateCompanyLeadContractEquipmentCategory(newValue as Option)
              setCategoryOptionToAppend(newValue as Option);
            }}
            onInputChange={setSearchEquipmentCategory}
            options={equipmentCategoryOptions?.length
              ? equipmentCategoryOptions.filter(
                (value) => !contract?.contract_equipment_categories?.find(
                  ({ equipment_category }) => equipment_category.id === value.value
                )
              )
              : []}
            isDisabled={createPrivateCompanyLeadContractEquipmentCategoryIsPending || disableFields}
            isLoading={createPrivateCompanyLeadContractEquipmentCategoryIsPending}
          />

          {watch('contract.contract_equipment_categories')?.map(({ id, equipment_category, quantity }, index) => (
            <div key={equipment_category.id} className="flex gap-2">
              <Button
                type="button"
                variant="outline-error"
                className="size-[54px] mt-auto"
                onClick={() => {
                  deletePrivateCompanyLeadEquipmentCategory(id);
                  setCategoryIndexToRemove(index)
                }}
                disabled={disableFields}
              >
                <XIcon />
              </Button>
              <TextField
                label="Categoria"
                defaultValue={equipment_category.display_name}
                className="w-1/2"
                disabled
              />

              <Controller
                control={control}
                name={`contract.contract_equipment_categories.${index}.quantity`}
                render={({ field }) => (
                  <MaskedTextField
                    mask="0000"
                    label="Quantidade"
                    className="w-1/2"
                    {...field}
                    value={`${quantity}`}
                    errorMessage={errors?.contract?.contract_equipment_categories?.[index]?.quantity?.message || undefined}
                    onBlur={(e) => submittableOnBlur(e, field.onBlur)}
                    disabled={disableFields}
                    markAsRequired
                  />
                )}
              />
            </div>
          ))}

          <div className="flex flex-col items-end gap-1">
            <p className="font-light text-xs italic leading-3">Total de equipamentos</p>
            <p className="font-bold leading-5"> {defined_equipment_count} / {operational_information?.expected_equipment_quantity}</p>
          </div>
        </div>
      }
    />
  )
}
